import type WaButton from "@web.awesome.me/webawesome-pro/dist/components/button/button.js";
import htmx from "htmx.org";
import { css, html } from "lit";
import { type FormSubmissionHandler, formSubmissionHandler } from "../../form";
import { ValidationError } from "../../schemas/validation-error";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";
import type { WmNoteContent } from "./content.js";
import type { WmNoteTitle } from "./title.js";

export class WmNoteCreate extends WmElement {
  static readonly TAG_NAME = "wm-note-create";

  static styles = [
    hostStyles,
    css`
      form {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: var(--wa-space-m);
      }

      wa-button {
        align-self: start;
      }
    `,
  ];

  #autoPopulateTitle: boolean;
  #formSubmit: FormSubmissionHandler;

  constructor() {
    super();
    // Starts at true since the title's input value is not set.
    this.#autoPopulateTitle = true;
    this.#formSubmit = formSubmissionHandler("/app/notes", {
      method: "POST",
    })
      .on("pre:response", () => (this.#querySubmitButton().loading = true))
      .on("response", (r) => this.#handleResponse(r))
      .on("post:response", () => (this.#querySubmitButton().loading = false))
      .on("error", (err) =>
        this.logger.error("unable to POST /app/notes", err),
      );
  }

  protected override render() {
    return html`
      <form @submit=${{ handleEvent: this.#formSubmit }}>
        <wm-note-title
          @blur=${this.#handleTitleBlur}
          @input=${this.#handleTitleInput}>
        </wm-note-title> 
        <wm-note-content
          @input=${this.#handleContentInput}>
        </wm-note-content>
        <wm-tags-combobox name="tags"></wm-tags-combobox>
        <wa-button variant="success" type="submit">Create</wa-button>
      </form>
    `;
  }

  async #handleResponse(response: Response) {
    // Handle 422 with JSON serialized error messages.
    if (response.status === 422) {
      const validationError = ValidationError.parse(await response.json());
      const { path, reason } = validationError.error;

      if (path === "title") {
        const titleInput = this.#queryTitleInput();
        titleInput.error = reason;
      } else if (path === "content") {
        const contentInput = this.#queryContentInput();
        contentInput.error = reason;
      } else {
        this.logger.warn(
          "unknown invalid field from 422 response: %s",
          path,
          validationError,
        );
      }
    } else {
      return htmx.ajax("GET", "/app/notes", { push: "true" });
    }
  }

  #handleContentInput(_: InputEvent) {
    const titleInput = this.#queryTitleInput();
    const contentInput = this.#queryContentInput();
    if (this.#autoPopulateTitle) {
      titleInput.value = contentInput.computedTitle;
    } else if (!titleInput.value && contentInput.value) {
      this.#autoPopulateTitle = true;
      titleInput.value = contentInput.computedTitle;
    }
  }

  async #handleTitleBlur(_: FocusEvent) {
    const titleInput = this.#queryTitleInput();
    const contentInput = this.#queryContentInput();
    await Promise.all([titleInput.updateComplete, contentInput.updateComplete]);
    if (!titleInput.value && contentInput.value) {
      this.#autoPopulateTitle = true;
      titleInput.value = contentInput.computedTitle;
    }
  }

  #handleTitleInput(_: InputEvent) {
    this.#autoPopulateTitle = false;
  }

  #queryTitleInput(): WmNoteTitle {
    return this.queryNotNil("wm-note-title");
  }

  #queryContentInput(): WmNoteContent {
    return this.queryNotNil("wm-note-content");
  }

  #querySubmitButton(): WaButton {
    return this.queryNotNil(`wa-button[type="submit"]`);
  }
}

customElements.define(WmNoteCreate.TAG_NAME, WmNoteCreate);

declare global {
  interface HTMLElementTagNameMap {
    [WmNoteCreate.TAG_NAME]: WmNoteCreate;
  }
}
